import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateEconRunMutation, useListEconRunsQuery } from '@shared/api';
import type { EconRun } from '@shared/api';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import {
  apiErrorMessage,
  ECON_MODULES,
  guessDocTypeByFilename,
  isMultipleAllowed,
  useEconPermission,
  type EconModuleId,
} from '@entities/economic/types';
import { AutoCollectFactModal } from '../AutoCollectFactModal';
import {
  Err,
  FileHint,
  FileList,
  FormTitle,
  Layout,
  MONTHS,
  PeriodRow,
  ReadOnlyHint,
  YEARS,
} from './styles';
import {
  type AttachedDoc,
  FileItemView,
  nextDocId,
  UNSET_TYPE,
} from './FilePicker';
import { RunsList } from './RunsList';

export function ModuleRunsView({ moduleId }: { moduleId: EconModuleId }) {
  const cfg = ECON_MODULES[moduleId];
  // Мета-модули без первички (М5 «Пакет руководству»): документы не грузятся,
  // прогон собирается из уже утверждённых расчётов. Прячем загрузчик и снимаем
  // требование «хотя бы один файл».
  const noFiles = cfg.docTypes.length === 0;
  const navigate = useNavigate();
  const { can_write } = useEconPermission();

  // Период НЕ дефолтим текущим месяцем: он должен соответствовать отчётному
  // периоду документов (КС за апрель ≠ «сегодня»). Пустые значения заставляют
  // выбрать осознанно; расхождение с документами подсветим в карточке обработки.
  // Два селекта вместо input[type=month]: Firefox month-инпут не поддерживает
  // (рендерит текстовое поле — свободный ввод уходил на backend и падал 400).
  const [periodMonth, setPeriodMonth] = useState<string>('');
  const [periodYear, setPeriodYear] = useState<string>('');
  const month = periodMonth && periodYear ? `${periodYear}-${periodMonth}` : '';
  const [scope, setScope] = useState('');
  const [docs, setDocs] = useState<AttachedDoc[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [autoCollectOpen, setAutoCollectOpen] = useState(false);

  const listQ = useListEconRunsQuery(
    { module: moduleId },
    {
      // Статус/прогресс обработок прилетает по WS-событию `econ_run_updated`
      // (rtkBridge инвалидирует generic-тег 'EconRun' → RTK перетягивает этот
      // список). Поэтому частый poll не нужен — оставляем редкий страховочный
      // рефетч (60с) на случай потери WS-соединения; создание/решение всё
      // равно инвалидируют тег напрямую.
      pollingInterval: 60000,
    },
  );

  const [createRun, { isLoading: creating }] = useCreateEconRunMutation();

  const runs = useMemo<EconRun[]>(() => listQ.data?.items ?? [], [listQ.data]);

  /** Принят как accept у общей кнопки: объединение форматов всех типов модуля. */
  const acceptAll = useMemo(() => {
    const exts = new Set<string>();
    for (const dt of cfg.docTypes) for (const ext of dt.accept.split(',')) exts.add(ext.trim());
    return Array.from(exts).join(',');
  }, [cfg.docTypes]);

  /** Типы, где несколько файлов — вероятная ОШИБКА выбора. Исключаем типы с
   *  допустимой множественностью (КС-2/КС-3: backend обрабатывает ВСЕ
   *  загруженные акты/книги за период — это норма, а не дубль). */
  const duplicateTypes = useMemo(() => {
    const count = new Map<string, number>();
    for (const d of docs) {
      if (!d.docType) continue;
      count.set(d.docType, (count.get(d.docType) ?? 0) + 1);
    }
    return new Set(
      Array.from(count.entries())
        .filter(([type, n]) => n > 1 && !isMultipleAllowed(type, cfg.docTypes))
        .map(([t]) => t),
    );
  }, [docs, cfg.docTypes]);

  /** Есть ли файлы без выбранного типа — submit с ними блокируется. */
  const hasUnsetType = useMemo(() => docs.some((d) => !d.docType), [docs]);

  /** Файлы из зоны загрузки: перетаскивание, выбор в диалоге, папка целиком
   *  или архив — `FileDropZone` сводит все способы к плоскому списку. */
  function handleAddFiles(files: File[]) {
    if (files.length === 0) return;
    setError(null);
    setDocs((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: nextDocId(),
        file,
        // Осмысленный дефолт по имени файла (КС-2/КС-3/Выполнение), иначе —
        // пустой выбор: пользователь сам укажет тип, без вводящей в
        // заблуждение позиционной раздачи «первый свободный».
        docType: guessDocTypeByFilename(file.name, cfg.docTypes) ?? UNSET_TYPE,
      })),
    ]);
  }

  function setDocType(id: string, docType: string) {
    setDocs((prev) => prev.map((d) => (d.id === id ? { ...d, docType } : d)));
  }

  function removeDoc(id: string) {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!month) {
      setError('Укажите отчётный период — выберите месяц и год.');
      return;
    }
    if (!noFiles) {
      if (docs.length === 0) {
        setError('Приложите хотя бы один документ и выберите его тип.');
        return;
      }
      if (hasUnsetType) {
        setError('Для каждого документа выберите тип — у некоторых файлов он не определён.');
        return;
      }
    }
    const fd = new FormData();
    fd.append('module', moduleId);
    // input[type=month] даёт YYYY-MM; backend ждёт YYYY-MM-DD (первое число).
    fd.append('period', `${month}-01`);
    if (scope.trim()) fd.append('object_scope', scope.trim());
    // Контракт сохранён: каждый файл уходит под именем поля = его docType.
    for (const d of docs) fd.append(d.docType, d.file);
    try {
      const res = await createRun(fd).unwrap();
      setDocs([]);
      setScope('');
      navigate(`/agents/econ/runs/${res.run_id}`);
    } catch (err) {
      // Показываем сообщение backend'а как есть (оно внятное и на русском:
      // «недопустимый тип документа…», «period должен быть в формате…»).
      // Прежний общий текст «проверьте формат документов» вводил в
      // заблуждение, когда проблема была вовсе не в файлах.
      setError(
        apiErrorMessage(err) ??
          'Не удалось запустить обработку. Попробуйте снова; если ошибка повторяется — обратитесь к администратору.',
      );
      // eslint-disable-next-line no-console
      console.error('createEconRun failed', err);
    }
  }

  return (
    <Layout>
      <Card>
        <FormTitle>
          {noFiles ? 'Сборка пакета' : 'Новая обработка'} · {cfg.short}
        </FormTitle>
        <form onSubmit={handleSubmit}>
          <Field>
            <Label htmlFor="econ-period-month">Отчётный период</Label>
            <PeriodRow>
              <Select
                id="econ-period-month"
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
                aria-describedby="econ-period-hint"
                aria-label="Месяц отчётного периода"
              >
                <option value="" disabled>
                  — месяц —
                </option>
                {MONTHS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </Select>
              <Select
                id="econ-period-year"
                value={periodYear}
                onChange={(e) => setPeriodYear(e.target.value)}
                aria-describedby="econ-period-hint"
                aria-label="Год отчётного периода"
              >
                <option value="" disabled>
                  — год —
                </option>
                {YEARS.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Select>
            </PeriodRow>
            <FileHint id="econ-period-hint">
              Месяц, ЗА который собирается отчёт — возьмите его из строки «Отчётный период» в шапке
              КС-2/КС-3. Если он не совпадёт с периодом из самих документов, мы подсветим
              расхождение в карточке обработки.
            </FileHint>
          </Field>
          {noFiles && (
            <FileHint style={{ marginBottom: 12 }}>
              Пакет собирается автоматически из уже утверждённых расчётов М1–М4 за
              выбранный период — документы прикладывать не нужно. Если каких-то
              направлений ещё нет, пакет соберётся неполным — это будет видно в
              карточке пакета.
            </FileHint>
          )}

          {!noFiles && (
            <Field>
              <Label htmlFor="econ-scope">Объект / участок (необязательно)</Label>
              <Input
                id="econ-scope"
                placeholder="Например: Карьер №2"
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              />
            </Field>
          )}

          {!noFiles && (
            <Field>
              <Label>Документы первички</Label>
              {can_write ? (
                <>
                  <FileDropZone
                    multiple
                    compact
                    accept={acceptAll}
                    hint="Или нажмите, чтобы выбрать. Тип документа укажете для каждого файла"
                    onFiles={handleAddFiles}
                  />
                  <FileHint>
                    Загружать все типы не обязательно — сколько есть. Поддерживаются таблицы (1С,
                    реестры), сканы PDF и отчёты DOCX.
                  </FileHint>
                </>
              ) : (
                <ReadOnlyHint>Только просмотр — загрузка документов недоступна.</ReadOnlyHint>
              )}

              {docs.length > 0 && (
                <FileList>
                  {docs.map((d) => (
                    <FileItemView
                      key={d.id}
                      doc={d}
                      cfg={cfg}
                      isDuplicate={duplicateTypes.has(d.docType)}
                      onRemove={removeDoc}
                      onChangeType={setDocType}
                    />
                  ))}
                </FileList>
              )}
            </Field>
          )}

          {error && <Err>{error}</Err>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button
              type="submit"
              $variant="primary"
              disabled={creating || !can_write || hasUnsetType}
              title={
                !can_write
                  ? 'Только просмотр'
                  : hasUnsetType
                    ? 'У некоторых документов не выбран тип'
                    : undefined
              }
            >
              {creating
                ? noFiles
                  ? 'Сборка…'
                  : 'Запуск…'
                : noFiles
                  ? 'Собрать пакет'
                  : 'Запустить расчёт'}
            </Button>
            {moduleId === 'bdr' && can_write && (
              <Button
                type="button"
                $variant="secondary"
                disabled={creating}
                onClick={() => {
                  if (!month) {
                    setError('Укажите отчётный период — выберите месяц и год перед автосбором.');
                    return;
                  }
                  setError(null);
                  setAutoCollectOpen(true);
                }}
              >
                Собрать факт из системы
              </Button>
            )}
          </div>
        </form>
      </Card>

      {autoCollectOpen && month && (
        <AutoCollectFactModal
          period={`${month}-01`}
          objectScope={scope}
          onClose={() => setAutoCollectOpen(false)}
          onCreated={(runId) => {
            setAutoCollectOpen(false);
            navigate(`/agents/econ/runs/${runId}`);
          }}
        />
      )}

      <RunsList
        runs={runs}
        isFetching={listQ.isFetching}
        isLoading={listQ.isLoading}
      />
    </Layout>
  );
}
